import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, formatError } from "../api";
import {
  ArrowLeft, CheckCircle2, Circle, Calendar, MapPin, User, Mail,
  Loader2, Upload, Plus, FileText, Trash2, Save, ShieldCheck,
} from "lucide-react";

const STATUS_COLORS = {
  pending: "#0099FF", in_progress: "#0040A8",
  ready_to_complete: "#16A34A", completed: "#0F7C39",
};

function humanSize(b) {
  if (!Number.isFinite(b)) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
}

export default function CompanyOnboardingDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [rec, setRec] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    setErr("");
    try {
      const r = await api.get(`/onboarding/${id}`);
      setRec(r.data);
      setForm({
        joining_date: r.data.joining_date || "",
        reporting_time: r.data.reporting_time || "",
        work_location: r.data.work_location || "",
        work_mode: r.data.work_mode || "",
        reporting_manager: r.data.reporting_manager || "",
        manager_email: r.data.manager_email || "",
        notes: r.data.notes || "",
      });
    } catch (e) {
      setErr(formatError(e));
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const saveDetails = async (e) => {
    e?.preventDefault?.();
    setBusy(true); setErr(""); setMsg("");
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ""));
      await api.patch(`/onboarding/${id}`, payload);
      setMsg("Joining details saved.");
      await load();
    } catch (e2) {
      setErr(formatError(e2));
    } finally {
      setBusy(false);
    }
  };

  const toggleTask = async (task) => {
    try {
      await api.patch(`/onboarding/${id}/tasks/${task.id}`, { done: !task.done });
      await load();
    } catch (e) { setErr(formatError(e)); }
  };

  const addTask = async () => {
    const label = window.prompt("New task description:");
    if (!label || !label.trim()) return;
    try {
      await api.post(`/onboarding/${id}/tasks`, { label: label.trim(), required: false });
      await load();
    } catch (e) { setErr(formatError(e)); }
  };

  const uploadDoc = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", "other");
    setBusy(true); setErr("");
    try {
      await api.post(`/onboarding/${id}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await load();
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const complete = async () => {
    if (!window.confirm("Mark this onboarding as complete? This will set the application status to `onboarded`.")) return;
    setBusy(true); setErr("");
    try {
      await api.post(`/onboarding/${id}/complete`);
      await load();
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!rec) {
    return (
      <div className="card card-pad text-sm text-muted flex items-center gap-2">
        {err ? err : <><Loader2 size={14} className="animate-spin" /> Loading onboarding…</>}
      </div>
    );
  }

  const meta = STATUS_COLORS[rec.status] || "#94A3B8";
  const tasks = rec.tasks || [];
  const documents = rec.documents || [];

  return (
    <div className="space-y-5" data-testid="onboarding-detail">
      <div className="flex items-center justify-between gap-3">
        <button type="button" onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-sm font-semibold text-signal" data-testid="onboarding-back">
          <ArrowLeft size={14} /> Back
        </button>
        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded text-white" style={{ background: meta }}>
          {rec.status?.replace("_", " ")}
        </span>
      </div>

      <div className="card card-pad">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="w-12 h-12 rounded-full grid place-items-center text-white text-sm font-bold shrink-0"
            style={{ background: "linear-gradient(135deg, #006CFF 0%, #9933FF 100%)" }}>
            {(rec.student_name || "?").split(/\s+/).slice(0, 2).map(w => w[0]).join("").toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="font-display font-extrabold text-xl">{rec.student_name}</div>
            <div className="text-sm text-muted">{rec.posting_title} · {rec.company_name}</div>
            <div className="text-xs text-muted inline-flex items-center gap-1 mt-1"><Mail size={11} /> {rec.student_email}</div>
          </div>
        </div>
      </div>

      {err && <div className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{err}</div>}
      {msg && <div className="text-sm text-emerald-700 bg-emerald-50 rounded-xl px-3 py-2">{msg}</div>}

      <div className="grid lg:grid-cols-3 gap-5">
        {/* ----------------- Joining details ----------------- */}
        <form onSubmit={saveDetails} className="card card-pad lg:col-span-1 space-y-3">
          <h2 className="font-display font-extrabold text-lg flex items-center gap-2"><Calendar size={16} /> Joining details</h2>
          <div>
            <label className="eyebrow">Joining date</label>
            <input
              type="date"
              className="input mt-1.5"
              value={form.joining_date}
              onChange={(e) => setForm({ ...form, joining_date: e.target.value })}
              data-testid="onb-joining-date"
            />
          </div>
          <div>
            <label className="eyebrow">Reporting time</label>
            <input
              className="input mt-1.5"
              value={form.reporting_time}
              onChange={(e) => setForm({ ...form, reporting_time: e.target.value })}
              placeholder="10:00 IST"
              data-testid="onb-reporting-time"
            />
          </div>
          <div>
            <label className="eyebrow">Work location</label>
            <input
              className="input mt-1.5"
              value={form.work_location}
              onChange={(e) => setForm({ ...form, work_location: e.target.value })}
              placeholder="Office address / city"
              data-testid="onb-work-location"
            />
          </div>
          <div>
            <label className="eyebrow">Work mode</label>
            <select
              className="input mt-1.5"
              value={form.work_mode}
              onChange={(e) => setForm({ ...form, work_mode: e.target.value })}
              data-testid="onb-work-mode"
            >
              <option value="">—</option>
              <option value="onsite">Onsite</option>
              <option value="remote">Remote</option>
              <option value="hybrid">Hybrid</option>
            </select>
          </div>
          <div>
            <label className="eyebrow">Reporting manager</label>
            <input
              className="input mt-1.5"
              value={form.reporting_manager}
              onChange={(e) => setForm({ ...form, reporting_manager: e.target.value })}
              placeholder="Priya Sharma"
              data-testid="onb-manager-name"
            />
          </div>
          <div>
            <label className="eyebrow">Manager email</label>
            <input
              type="email"
              className="input mt-1.5"
              value={form.manager_email}
              onChange={(e) => setForm({ ...form, manager_email: e.target.value })}
              placeholder="priya@company.com"
              data-testid="onb-manager-email"
            />
          </div>
          <div>
            <label className="eyebrow">Internal notes</label>
            <textarea
              className="input mt-1.5 min-h-[80px]"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="onb-notes"
            />
          </div>
          <button type="submit" disabled={busy} className="btn-primary inline-flex items-center gap-2 disabled:opacity-60" data-testid="onb-save-details">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save details
          </button>
        </form>

        {/* ----------------- Checklist + docs ----------------- */}
        <div className="lg:col-span-2 space-y-5">
          <div className="card card-pad">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display font-extrabold text-lg flex items-center gap-2"><CheckCircle2 size={16} /> Checklist</h2>
              <button type="button" onClick={addTask} className="text-xs font-semibold text-signal inline-flex items-center gap-1" data-testid="onb-add-task">
                <Plus size={12} /> Add task
              </button>
            </div>
            <ul className="space-y-1.5">
              {tasks.map((t) => (
                <li key={t.id} className="flex items-center gap-3 p-2 rounded-md hover:bg-paper transition" data-testid={`task-${t.id}`}>
                  <button
                    type="button"
                    onClick={() => toggleTask(t)}
                    aria-label={t.done ? "Mark incomplete" : "Mark complete"}
                    data-testid={`task-toggle-${t.id}`}
                    className="shrink-0"
                  >
                    {t.done
                      ? <CheckCircle2 size={20} className="text-emerald-600" />
                      : <Circle size={20} className="text-muted" />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className={`text-sm ${t.done ? "line-through text-muted" : ""}`}>{t.label}</div>
                    {t.note && <div className="text-xs text-muted mt-0.5">{t.note}</div>}
                  </div>
                  {t.required && (
                    <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Required</span>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div className="card card-pad">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display font-extrabold text-lg flex items-center gap-2"><FileText size={16} /> Documents ({documents.length})</h2>
              <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className="btn-secondary inline-flex items-center gap-1.5 text-xs" data-testid="onb-upload-doc">
                {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Upload
              </button>
              <input
                ref={fileRef}
                type="file"
                className="hidden"
                accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx"
                onChange={(e) => uploadDoc(e.target.files?.[0])}
              />
            </div>
            {documents.length === 0 ? (
              <div className="text-sm text-muted text-center py-4">No documents uploaded yet.</div>
            ) : (
              <ul className="space-y-2">
                {documents.map((d) => (
                  <li key={d.id} className="flex items-center gap-3 p-2 rounded-md border" style={{ borderColor: "var(--rule)" }} data-testid={`doc-${d.id}`}>
                    <FileText size={16} className="text-signal shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate">{d.original_filename}</div>
                      <div className="text-[11px] text-muted">{humanSize(d.size)} · {new Date(d.uploaded_at).toLocaleString()}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {rec.status !== "completed" && (
            <div className="card card-pad bg-paper">
              <div className="flex items-center gap-3 flex-wrap">
                <ShieldCheck size={20} className="text-emerald-700" />
                <div className="flex-1 min-w-[200px]">
                  <div className="font-semibold text-sm">Ready to welcome them aboard?</div>
                  <div className="text-xs text-muted">All required tasks must be done first.</div>
                </div>
                <button type="button" onClick={complete} disabled={busy} className="btn-primary inline-flex items-center gap-2 disabled:opacity-60" data-testid="onb-complete">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Mark complete
                </button>
              </div>
            </div>
          )}

          {rec.status === "completed" && rec.completed_at && (
            <div className="card card-pad bg-emerald-50 border-emerald-200">
              <div className="flex items-center gap-3">
                <CheckCircle2 size={20} className="text-emerald-700" />
                <div>
                  <div className="font-semibold text-sm text-emerald-900">Onboarded</div>
                  <div className="text-xs text-emerald-800">Completed {new Date(rec.completed_at).toLocaleString()}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
